import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listActivity, type ActivityItem } from "@/lib/activity.functions";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export const Route = createFileRoute("/_authenticated/gestion/actividad")({
  head: () => ({ meta: [{ title: "Actividad · Panel" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: ActividadPage,
});

const CATEGORIAS: { value: string; label: string }[] = [
  { value: "todos", label: "Todas las categorías" },
  { value: "reserva", label: "Reservas" },
  { value: "pago", label: "Pagos" },
  { value: "factura", label: "Facturas" },
  { value: "enlace_pago", label: "Enlaces de pago" },
  { value: "bloqueo", label: "Bloqueos" },
  { value: "limpieza", label: "Limpieza" },
  { value: "admin", label: "Administradores" },
];

const ORIGEN_COLOR: Record<string, string> = {
  panel: "bg-blue-100 text-blue-800",
  web_publica: "bg-green-100 text-green-800",
  redsys: "bg-purple-100 text-purple-800",
  trigger: "bg-slate-100 text-slate-700",
  sistema: "bg-amber-100 text-amber-800",
  checkout_automatico: "bg-amber-100 text-amber-800",
  manual: "bg-blue-100 text-blue-800",
};

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function ActividadPage() {
  const call = useServerFn(listActivity);
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoria, setCategoria] = useState("todos");
  const [search, setSearch] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  async function load() {
    setLoading(true);
    try {
      const rows = await call({
        data: {
          categoria,
          search: search || undefined,
          desde: desde ? new Date(desde).toISOString() : undefined,
          hasta: hasta ? new Date(hasta + "T23:59:59").toISOString() : undefined,
          limit: 300,
        },
      });
      setItems(rows);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [categoria]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl">Registro de actividad</h1>
          <p className="text-sm text-muted-foreground">Historial de cambios realizados en el panel y por el sistema.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label>Categoría</Label>
            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIAS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Buscar</Label>
            <Input
              value={search}
              placeholder="Nombre, importe, motivo…"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            />
          </div>
          <div>
            <Label>Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div>
            <Label>Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <Button onClick={load} disabled={loading}>Aplicar filtros</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-3">Cuándo</th>
                <th>Evento</th>
                <th>Descripción</th>
                <th>Usuario</th>
                <th>Origen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t align-top">
                  <td className="p-3 whitespace-nowrap text-muted-foreground">{formatDate(it.created_at)}</td>
                  <td className="p-3 whitespace-nowrap"><code className="rounded bg-secondary px-1.5 py-0.5 text-xs">{it.event_type}</code></td>
                  <td className="p-3">{it.summary}</td>
                  <td className="p-3 text-muted-foreground">{it.actor_email ?? <span className="italic">sistema</span>}</td>
                  <td className="p-3">
                    <Badge variant="outline" className={ORIGEN_COLOR[it.origen] ?? ""}>{it.origen}</Badge>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Sin actividad para estos filtros.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Cargando…</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}